// src/scenes/dashboard/algorithms/refrigerantShortage.js

const SUCTION_PRESSURE_LOW_PSI = 31;
const DISCHARGE_TEMP_HIGH_C = 76;
const SUPERHEAT_HIGH_C = 15; // equivalente a 27°F de diferencial
const REQUIRED_CONSECUTIVE_RECORDS = 3;

const STANDARD_ATMOSPHERIC_PRESSURE_PSI = 14.6959;

function toAlgorithmNumber(value) {
  if (value == null || value === "") return null;
  const n = Number(String(value).trim().replace(",", "."));
  return Number.isFinite(n) ? n : null;
}

function parseCompressorOnState(value) {
  if (value == null || value === "") return null;
  const s = String(value).trim().toLowerCase();
  if (["presente", "on", "encendido", "true", "1", "sí", "si"].includes(s)) return true;
  if (["ausente", "off", "apagado", "false", "0", "no"].includes(s)) return false;
  return null;
}

function psiaToPsig(value) {
  const pressurePsia = toAlgorithmNumber(value);
  if (pressurePsia == null) return null;
  return Number((pressurePsia - STANDARD_ATMOSPHERIC_PRESSURE_PSI).toFixed(2));
}

function getRowTimestamp(row) {
  const value = row?.fecha;
  if (value instanceof Date) return value.getTime();
  if (value == null || value === "") return 0;
  const normalizedValue = String(value).trim().replace(" ", "T");
  const timestamp = Date.parse(normalizedValue);
  return Number.isFinite(timestamp) ? timestamp : 0;
}

function evaluateRefrigerantShortageCircuit({
  machineKey,
  machineTitle,
  compressorNumber,
  suctionPressure,
  dischargeTemperature,
  superheat,
  compressorLoad,
  compressorOn,
  hasSuperheatSensor = true,
  hasDischargeTempSensor = true,
}) {
  const pressurePsi = toAlgorithmNumber(suctionPressure);
  const dischargeTempC = hasDischargeTempSensor
    ? toAlgorithmNumber(dischargeTemperature)
    : null;
  const load = toAlgorithmNumber(compressorLoad);

  // Compuerta real: señal booleana "Encendido Compresor" del PLC.
  const isOn = parseCompressorOnState(compressorOn);

  // El PLC manda un valor negativo en sobrecalentamiento cuando no hay
  // comunicación del sensor. No es una lectura real, así que lo tratamos
  // como dato no disponible para este campo específico.
  const superheatRaw = hasSuperheatSensor ? toAlgorithmNumber(superheat) : null;
  const superheatC =
    superheatRaw != null && superheatRaw >= 0 && superheatRaw <= 100
      ? superheatRaw
      : null;
  const superheatAvailable = hasSuperheatSensor && superheatC != null;

  const key = `${machineKey}_refrigerant_shortage_c${compressorNumber}`;
  const title = `${machineTitle} Posible falta de refrigerante C${compressorNumber}`;

  if (isOn == null) {
    return {
      key,
      title,
      status: "nodata",
      message: "No hay señal de encendido del compresor.",
      cause: `No fue posible determinar si el compresor ${compressorNumber} está encendido.`,
      resultLabel: "Resultado",
      deltaT: null,
      expectedMin: null,
      expectedMax: null,
      alarmMin: null,
      loads: [],
    };
  }

  // Compresor apagado. No genera tarjeta.
  if (!isOn) {
    return null;
  }

  if (
    pressurePsi == null ||
    (hasDischargeTempSensor && dischargeTempC == null)
  ) {
    return {
      key,
      title,
      status: "nodata",
      message: "No hay suficientes datos para evaluar.",
      cause: hasDischargeTempSensor
        ? "Falta presión de succión o temperatura de descarga."
        : "Falta presión de succión.",
      resultLabel: "Resultado",
      deltaT: null,
      expectedMin: null,
      expectedMax: null,
      alarmMin: null,
      loads: load != null ? [load] : [],
    };
  }

  // Condiciones que esta lectura permite evaluar. Con menos de 2 no se
  // puede confirmar nada bajo la regla de "2 de 3".
  const evaluableConditions =
    1 + (hasDischargeTempSensor ? 1 : 0) + (superheatAvailable ? 1 : 0);

  if (evaluableConditions < 2) {
    return {
      key,
      title,
      status: "nodata",
      message: "No hay suficientes condiciones para evaluar.",
      cause:
        "Esta máquina solo evalúa presión de succión y sobrecalentamiento, y el sobrecalentamiento no está comunicando. Con una sola condición no se puede confirmar falta de refrigerante.",
      resultLabel: "Resultado",
      deltaT: null,
      expectedMin: null,
      expectedMax: null,
      alarmMin: null,
      loads: load != null ? [load] : [],
    };
  }

  const lowSuctionPressure = pressurePsi < SUCTION_PRESSURE_LOW_PSI;
  const highDischargeTemp =
    hasDischargeTempSensor && dischargeTempC >= DISCHARGE_TEMP_HIGH_C;
  const highSuperheat = superheatAvailable && superheatC >= SUPERHEAT_HIGH_C;

  const trueConditionsCount = [
    lowSuctionPressure,
    highDischargeTemp,
    highSuperheat,
  ].filter(Boolean).length;

  // Se requieren al menos 2 de las 3 condiciones para considerar la alarma.
  const anyConditionTrue = trueConditionsCount >= 2;

  const superheatText = !hasSuperheatSensor
    ? null
    : superheatAvailable
      ? `${superheatC.toFixed(1)} °C`
      : "sin comunicación";

  let calculationDetails = `Presión de succión: ${pressurePsi.toFixed(1)} psi.`;

  if (hasDischargeTempSensor) {
    calculationDetails += ` Temperatura de descarga: ${dischargeTempC.toFixed(1)} °C.`;
  }

  if (hasSuperheatSensor) {
    calculationDetails += ` Sobrecalentamiento: ${superheatText}.`;
  }

  const reasons = [];
  if (lowSuctionPressure)
    reasons.push(`presión de succión baja (< ${SUCTION_PRESSURE_LOW_PSI} psi)`);
  if (highDischargeTemp)
    reasons.push(`temperatura de descarga alta (>= ${DISCHARGE_TEMP_HIGH_C}°C)`);
  if (highSuperheat)
    reasons.push(`sobrecalentamiento alto (>= ${SUPERHEAT_HIGH_C}°C)`);

  let status = "ok";
  let message = "Sin indicios de falta de refrigerante o fuga.";
  let resultLabel = "Resultado";
  let cause =
    trueConditionsCount === 1
      ? `1 condición fuera de rango (${reasons[0]}); se requieren al menos 2 para indicar falta de refrigerante. ${calculationDetails}`
      : `Ninguna condición fuera de rango. ${calculationDetails}`;

  if (hasSuperheatSensor && !superheatAvailable) {
    cause = `${cause} Nota: el sensor de sobrecalentamiento no está comunicando, esa condición no se pudo evaluar.`;
  }

  if (anyConditionTrue) {
    status = "alarm";
    message = "Posible falta de refrigerante / posible presencia de fuga.";
    resultLabel = "Posible causa";

    cause =
      `Se detectaron ${trueConditionsCount} de 3 condiciones fuera de rango (${reasons.join(", ")}). ` +
      `Posible falta de refrigerante por fuga o falla en EXV. ${calculationDetails}`;
  }

  const metrics = [
    {
      label: "Presión succión",
      value: `${pressurePsi.toFixed(1)} psi`,
      alarm: lowSuctionPressure,
    },
  ];

  if (hasDischargeTempSensor) {
    metrics.push({
      label: "Temp. descarga",
      value: `${dischargeTempC.toFixed(1)} °C`,
      alarm: highDischargeTemp,
    });
  }

  if (hasSuperheatSensor) {
    metrics.push({
      label: "Sobrecalentamiento",
      value: superheatText,
      alarm: highSuperheat,
    });
  }

  return {
    key,
    title,
    status,
    message,
    cause,
    resultLabel,
    deltaT: null,
    expectedMin: null,
    expectedMax: null,
    alarmMin: null,
    loads: load != null ? [load] : [],
    metrics,
    compressorNumber,
    suctionPressurePsi: pressurePsi,
    dischargeTemperatureC: dischargeTempC,
    superheatC,
    superheatAvailable,
    lowSuctionPressure,
    highDischargeTemp,
    highSuperheat,
    trueConditionsCount,
  };
}

function evaluateRefrigerantShortageTrend({
  rows,
  machineKey,
  machineTitle,
  compressorNumber,
  suctionPressureField,
  dischargeTemperatureField,
  superheatField,
  compressorLoadField,
  compressorOnField,
  hasSuperheatSensor = true,
  hasDischargeTempSensor = true,
}) {
  const orderedRows = Array.isArray(rows)
    ? [...rows]
        .filter(Boolean)
        .sort((a, b) => getRowTimestamp(b) - getRowTimestamp(a))
    : [];

  const recentRows = orderedRows.slice(0, REQUIRED_CONSECUTIVE_RECORDS);

  if (recentRows.length === 0) return null;

  const evaluations = recentRows.map((row) =>
    evaluateRefrigerantShortageCircuit({
      machineKey,
      machineTitle,
      compressorNumber,
      suctionPressure: row?.[suctionPressureField],
      dischargeTemperature: hasDischargeTempSensor
        ? row?.[dischargeTemperatureField]
        : null,
      superheat: hasSuperheatSensor ? row?.[superheatField] : null,
      compressorLoad: row?.[compressorLoadField],
      compressorOn: row?.[compressorOnField],
      hasSuperheatSensor,
      hasDischargeTempSensor,
    })
  );

  const currentResult = evaluations[0];

  // Compresor apagado en el registro actual.
  if (!currentResult) return null;

  let consecutiveAlarmCount = 0;
  for (const result of evaluations) {
    const isAlarmCondition = result && result.status === "alarm";
    if (!isAlarmCondition) break;
    consecutiveAlarmCount += 1;
  }

  const alarmConfirmed =
    recentRows.length === REQUIRED_CONSECUTIVE_RECORDS &&
    consecutiveAlarmCount === REQUIRED_CONSECUTIVE_RECORDS;

  const trendDetails =
    `Registros consecutivos con condición de alarma: ` +
    `${consecutiveAlarmCount} de ${REQUIRED_CONSECUTIVE_RECORDS}.`;

  if (currentResult.status === "nodata") {
    return {
      ...currentResult,
      cause: `${currentResult.cause} ${trendDetails}`,
      consecutiveAlarmCount,
      requiredConsecutiveRecords: REQUIRED_CONSECUTIVE_RECORDS,
      alarmConfirmed: false,
    };
  }

  // Cumple la condición ahora mismo, pero todavía no lleva 3 seguidas.
  if (currentResult.status === "alarm" && !alarmConfirmed) {
    return {
      ...currentResult,
      status: "warn",
      message: "Condición de posible falta de refrigerante en observación.",
      resultLabel: "Seguimiento",
      cause:
        `La lectura actual cumple condición de alarma, pero todavía no existen ` +
        `${REQUIRED_CONSECUTIVE_RECORDS} registros consecutivos para confirmarla. ` +
        `${currentResult.cause} ${trendDetails}`,
      consecutiveAlarmCount,
      requiredConsecutiveRecords: REQUIRED_CONSECUTIVE_RECORDS,
      alarmConfirmed: false,
    };
  }

  return {
    ...currentResult,
    cause: `${currentResult.cause} ${trendDetails}`,
    consecutiveAlarmCount,
    requiredConsecutiveRecords: REQUIRED_CONSECUTIVE_RECORDS,
    alarmConfirmed,
  };
}

export function evaluateOptichiller2CompRefrigerantShortage(rows) {
  const results = [
    evaluateRefrigerantShortageTrend({
      rows,
      machineKey: "optichiller2comp",
      machineTitle: "Optichiller 2Comp",
      compressorNumber: 1,
      suctionPressureField: "presionSuccionComp1",
      dischargeTemperatureField: "tempDescargaComp1",
      superheatField: "sobrecalentamientoComp1",
      compressorLoadField: "porcentajeCargaComp1",
      compressorOnField: "encendidoComp1",
    }),

    evaluateRefrigerantShortageTrend({
      rows,
      machineKey: "optichiller2comp",
      machineTitle: "Optichiller 2Comp",
      compressorNumber: 2,
      suctionPressureField: "presionSuccionComp2",
      dischargeTemperatureField: "tempDescargaComp2",
      superheatField: "sobrecalentamientoComp2",
      compressorLoadField: "porcentajeCargaComp2",
      compressorOnField: "encendidoComp2",
    }),
  ];

  return results.filter(Boolean);
}

export function evaluateCentrifugaRefrigerantShortage(rows) {
  const convertedRows = Array.isArray(rows)
    ? rows.map((row) => {
        const slideValve = toAlgorithmNumber(row?.posicionSlideValve);
        const slideValveValida =
          slideValve != null && slideValve >= 0 && slideValve <= 100
            ? slideValve
            : null;

        return {
          ...row,
          presionSuccionPsig: psiaToPsig(row?.presAbsolutaSuccion),
          cargaCentrifuga: slideValveValida,
        };
      })
    : [];

  const result = evaluateRefrigerantShortageTrend({
    rows: convertedRows,
    machineKey: "centrifuga",
    machineTitle: "Centrífuga",
    compressorNumber: 1,
    suctionPressureField: "presionSuccionPsig",
    dischargeTemperatureField: "tempDescarga",
    compressorLoadField: "cargaCentrifuga",
    compressorOnField: "encendidoCompresor",
    hasSuperheatSensor: false,
  });

  return result ? [result] : [];
}

export function evaluateOptichiller2CompCT6RefrigerantShortage(rows) {
  const results = [
    evaluateRefrigerantShortageTrend({
      rows,
      machineKey: "optichiller_ct6",
      machineTitle: "Optichiller 2Comp CT6",
      compressorNumber: 1,
      suctionPressureField: "presSuccionComp1",
      dischargeTemperatureField: "tempDescargaComp1",
      superheatField: "sobrecalentamientoComp1",
      compressorLoadField: "porcentajeCargaComp1",
      compressorOnField: "encendidoComp1",
    }),

    evaluateRefrigerantShortageTrend({
      rows,
      machineKey: "optichiller_ct6",
      machineTitle: "Optichiller 2Comp CT6",
      compressorNumber: 2,
      suctionPressureField: "presSuccionComp2",
      dischargeTemperatureField: "tempDescargaComp2",
      superheatField: "sobrecalentamientoComp2",
      compressorLoadField: "porcentajeCargaComp2",
      compressorOnField: "encendidoComp2",
    }),
  ];

  return results.filter(Boolean);
}

export function evaluateOptichiller1CompRefrigerantShortage(rows) {
  const result = evaluateRefrigerantShortageTrend({
    rows,
    machineKey: "optichiller1comp",
    machineTitle: "Optichiller 1Comp",
    compressorNumber: 1,
    suctionPressureField: "presionSuccionComp1",
    dischargeTemperatureField: "tempDescargaComp1",
    superheatField: "sobrecalentamientoComp1",
    compressorLoadField: "porcentajeCargaComp1",
    compressorOnField: "encendidoComp1",
  });

  return result ? [result] : [];
}

export function evaluateOptichiller1CompCT6RefrigerantShortage(rows) {
  const result = evaluateRefrigerantShortageTrend({
    rows,
    machineKey: "optichiller1comp_ct6",
    machineTitle: "Optichiller 1Comp CT6",
    compressorNumber: 1,
    suctionPressureField: "presSuccionComp1",
    dischargeTemperatureField: "tempDescargaComp1",
    superheatField: "sobrecalentamientoComp1",
    compressorLoadField: "porcentajeCargaComp1",
    compressorOnField: "encendidoComp1",
  });

  return result ? [result] : [];
}

export function evaluateCoTherm2CompRefrigerantShortage(rows) {
  const results = [
    evaluateRefrigerantShortageTrend({
      rows,
      machineKey: "cotherm2comp",
      machineTitle: "CoTherm 2Comp",
      compressorNumber: 1,
      suctionPressureField: "presSuccionComp1",
      dischargeTemperatureField: "tempDescargaComp1",
      superheatField: "sobrecalentamientoComp1",
      compressorLoadField: "porcentajeCargaComp1",
      compressorOnField: "encendidoComp1",
    }),

    evaluateRefrigerantShortageTrend({
      rows,
      machineKey: "cotherm2comp",
      machineTitle: "CoTherm 2Comp",
      compressorNumber: 2,
      suctionPressureField: "presSuccionComp2",
      dischargeTemperatureField: "tempDescargaComp2",
      superheatField: "sobrecalentamientoComp2",
      compressorLoadField: "porcentajeCargaComp2",
      compressorOnField: "encendidoComp2",
    }),
  ];

  return results.filter(Boolean);
}

export function evaluateEzchiller2CompCT6RefrigerantShortage(rows) {
  const results = [
    evaluateRefrigerantShortageTrend({
      rows,
      machineKey: "ezchiller2compct6",
      machineTitle: "ezChiller 2Comp CT6",
      compressorNumber: 1,
      suctionPressureField: "presionSuccionComp1",
      dischargeTemperatureField: "tempDescargaComp1",
      superheatField: "sobrecalentamientoComp1",
      compressorLoadField: "porcentajeCargaComp1",
      compressorOnField: "encendidoComp1",
    }),

    evaluateRefrigerantShortageTrend({
      rows,
      machineKey: "ezchiller2compct6",
      machineTitle: "ezChiller 2Comp CT6",
      compressorNumber: 2,
      suctionPressureField: "presionSuccionComp2",
      dischargeTemperatureField: "tempDescargaComp2",
      superheatField: "sobrecalentamientoComp2",
      compressorLoadField: "porcentajeCargaComp2",
      compressorOnField: "encendidoComp2",
    }),
  ];

  return results.filter(Boolean);
}

export function evaluateFSCRefrigerantShortage(rows) {
  // Cada módulo reporta su propio Cond_Actual (0-9). Solo "3 = Operando"
  // cuenta como compresor encendido para este algoritmo.
  const convertedRows = Array.isArray(rows)
    ? rows.map((row) => {
        const extra = {};
        for (let n = 1; n <= 10; n++) {
          const condActual = toAlgorithmNumber(row?.[`m${n}CondicionActual`]);
          extra[`m${n}Operando`] = condActual === 3 || condActual === 6 ? 1 : 0;
        }
        return { ...row, ...extra };
      })
    : [];

  const results = [];

  for (let n = 1; n <= 10; n++) {
    const result = evaluateRefrigerantShortageTrend({
      rows: convertedRows,
      machineKey: "fsc",
      machineTitle: "Full Service Cooling",
      compressorNumber: n,
      suctionPressureField: `m${n}PresSuccion`,
      dischargeTemperatureField: `m${n}TempDescarga`,
      compressorOnField: `m${n}Operando`,
      hasSuperheatSensor: false,
    });

    if (result) {
      results.push({
        ...result,
        key: `fsc_m${n}_refrigerant_shortage`,
        title: `Full Service Cooling M${n} Posible falta de refrigerante`,
      });
    }
  }

  return results;
}

export function evaluateFSC1CompRefrigerantShortage(rows) {
  const result = evaluateRefrigerantShortageTrend({
    rows,
    machineKey: "fsc_1comp",
    machineTitle: "FSC 1Comp",
    compressorNumber: 1,
    suctionPressureField: "presionSuccion",
    dischargeTemperatureField: "tempDescarga",
    superheatField: "sobrecalentamiento",
    compressorOnField: "encendidoComp1",
  });

  return result ? [result] : [];
}

export function evaluateLicuador1CompRefrigerantShortage(rows) {
  const result = evaluateRefrigerantShortageTrend({
    rows,
    machineKey: "licuador1comp",
    machineTitle: "Licuador 1Comp",
    compressorNumber: 1,
    suctionPressureField: "presSuccion",
    dischargeTemperatureField: "tempDescarga",
    superheatField: "sobrecalentamientoComp1",
    compressorLoadField: "porcentajeCargaComp1",
    compressorOnField: "encendidoComp1",
  });

  return result ? [result] : [];
}

export function evaluateVictexRefrigerantShortage(rows) {
  // En Victex, Cond_Actual 4 = Operando y 7 = Alarma (el compresor sigue
  // corriendo con una alarma activa). Cualquier otro estado cuenta como
  // apagado. Si el dato no llega, queda en null para que la tarjeta diga
  // "sin señal de encendido" en vez de desaparecer.
  const convertedRows = Array.isArray(rows)
    ? rows.map((row) => {
        const cond = toAlgorithmNumber(row?.condicionActual);
        return {
          ...row,
          operando: cond == null ? null : cond === 4 || cond === 7 ? 1 : 0,
        };
      })
    : [];

  const result = evaluateRefrigerantShortageTrend({
    rows: convertedRows,
    machineKey: "victex1comp",
    machineTitle: "Victex 1Comp",
    compressorNumber: 1,
    suctionPressureField: "presionSuccion",
    dischargeTemperatureField: "tempDescarga",
    compressorLoadField: "porcentajeCargaComp1",
    compressorOnField: "operando",
    hasSuperheatSensor: false,
  });

  return result ? [result] : [];
}

export function evaluateFSCV31CompRefrigerantShortage(rows) {
  // Esta máquina manda el sobrecalentamiento sin dividir entre 10
  // (85–110 = 8.5–11 °C). Lo convertimos aquí para no afectar el gauge.
  // Los negativos (-1 = sin comunicación) siguen siendo negativos.
  const convertedRows = Array.isArray(rows)
    ? rows.map((row) => {
        const sh = toAlgorithmNumber(row?.sobrecalentamientoComp1);
        return { ...row, sobrecalentamientoC: sh == null ? null : sh / 10 };
      })
    : [];

  const result = evaluateRefrigerantShortageTrend({
    rows: convertedRows,
    machineKey: "fsc_v3_1comp",
    machineTitle: "FSC V3 1Comp",
    compressorNumber: 1,
    suctionPressureField: "presionSuccionComp1",
    superheatField: "sobrecalentamientoC",
    compressorLoadField: "porcentajeCargaComp1",
    compressorOnField: "encendidoComp1",
    hasDischargeTempSensor: false,
  });

  return result ? [result] : [];
}

export function evaluateFSCV21CompRefrigerantShortage(rows) {
  // El sobrecalentamiento llega siempre en -1 (sin comunicación) en todos
  // los equipos. Se evalúa solo succión + descarga (deben cumplirse ambas).
  const result = evaluateRefrigerantShortageTrend({
    rows,
    machineKey: "fsc_v2_1comp",
    machineTitle: "FSC V2 1Comp",
    compressorNumber: 1,
    suctionPressureField: "presionSuccion",
    dischargeTemperatureField: "tempDescarga",
    compressorOnField: "encendidoComp1",
    hasSuperheatSensor: false,
  });

  return result ? [result] : [];
}

export function evaluateFSCUnicoRefrigerantShortage(rows) {
  const validRaw = (Array.isArray(rows) ? rows : [])
    .flatMap((row) => [row?.superheatC1, row?.superheatC2])
    .map(toAlgorithmNumber)
    .filter((v) => v != null && v >= 0 && v < 1000);

  const scale = validRaw.some((v) => v > 30) ? 10 : 1;

  const convertedRows = Array.isArray(rows)
    ? rows.map((row) => {
        const sh1 = toAlgorithmNumber(row?.superheatC1);
        const sh2 = toAlgorithmNumber(row?.superheatC2);
        return {
          ...row,
          superheatC1Celsius: sh1 == null ? null : sh1 / scale,
          superheatC2Celsius: sh2 == null ? null : sh2 / scale,
        };
      })
    : [];

  const results = [1, 2].map((n) =>
    evaluateRefrigerantShortageTrend({
      rows: convertedRows,
      machineKey: "fsc_unico",
      machineTitle: "FSC UNICO",
      compressorNumber: n,
      suctionPressureField: `presSuccionC${n}`,
      superheatField: `superheatC${n}Celsius`,
      compressorOnField: `encendidoComp${n}`,
      hasDischargeTempSensor: false,
    })
  );

  return results.filter(Boolean);
}